import typia from 'typia';
import { dispatchAction } from '@ardoq/rxbeach';
import type { LoadedState, MetaModel } from '@ardoq/api-types';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import type { AiControlledCommand, CommandExecutionState, CommandRejection, } from 'aiControlledCommands/types';
import { defineAiControlledCommand, type CommandValidationResult, } from 'aiControlledCommands/defineAiControlledCommand';
import { TRAVERSAL_BUILDING_DOMAIN } from '../domain';
import { traversalConstructed, traversalOpenRequested } from '../actions';
import { traversalConstructionOperations } from '../traversalConstructionOperations';
import { hasMatches, isBuildViewDatasetContext, isLoadedTraversal, isPseudoTraversal, } from '../predicates';
import type { BuildViewDatasetContext, PseudoTraversal, ResolvedPseudoTraversal, } from '../types';
import { findRealData } from './findRealData';
import { BUILD_VIEW_DATASET_CONTEXT_DESCRIPTION, WHEN_TO_USE, } from './descriptions';
const reject = (reason: string): CommandRejection => ({
    status: 'rejected',
    reason,
});
export const validatePseudoTraversal = (rawPayload: unknown, { loadedTraversals }: BuildViewDatasetContext, { loadedStates }: CommandExecutionState): CommandValidationResult<ResolvedPseudoTraversal> => {
    if (!isPseudoTraversal(rawPayload)) {
        return reject('The payload does not match the schema.');
    }
    const { startSet, paths } = rawPayload;
    if (!startSet.startSetInstanceNames.length) {
        return reject("'startSetInstanceNames' is empty. Name the specific components the traversal starts from.");
    }
    if (!paths.length || paths.some(path => !path.length)) {
        return reject("'paths' needs at least one path, and every path at least one step.");
    }
    const startNameKeys = new Set([
        ...startSet.startSetInstanceNames,
        ...(startSet.componentType ? [startSet.componentType] : []),
    ].map(traversalConstructionOperations.normalizeName));
    if (!paths.every(([firstTriple]) => startNameKeys.has(traversalConstructionOperations.normalizeName(traversalConstructionOperations.orderByWalkDirection(firstTriple)[0])))) {
        return reject("The first step of every path must walk from a name in 'startSetInstanceNames' or from 'componentType': its 'sourceTypeOrInstanceName' when 'outgoing', its 'targetTypeOrInstanceName' when 'incoming'.");
    }
    const activeTraversalIds = new Set(loadedTraversals.map(({ loadedTraversalId }) => loadedTraversalId));
    return {
        status: 'valid',
        payload: {
            pseudoTraversal: rawPayload,
            activeLoadedTraversals: loadedStates
                .filter(isLoadedTraversal)
                .filter(({ uuid }) => activeTraversalIds.has(uuid)),
        },
    };
};
const executeBuildViewDataset = async ({ pseudoTraversal, activeLoadedTraversals }: ResolvedPseudoTraversal, { metaModel }: CommandExecutionState): Promise<CommandRejection | undefined> => {
    const traversalMatches = traversalConstructionOperations.constructTraversal(await findRealData(pseudoTraversal, metaModel, activeLoadedTraversals));
    if (!traversalMatches.some(hasMatches)) {
        return reject('No real data matches the traversal: its start components or paths do not exist.');
    }
    dispatchAction(traversalConstructed(pseudoTraversal));
    const metamodelTraversal = traversalConstructionOperations.pickSingleMatchMetamodelTraversal(traversalMatches);
    if (metamodelTraversal) {
        dispatchAction(traversalOpenRequested({
            metamodelTraversal,
            loadedTraversal: traversalConstructionOperations.pickEditedLoadedTraversal(activeLoadedTraversals, metamodelTraversal.startType, pseudoTraversal.loadedTraversalId),
        }));
        return undefined;
    }
    // TODO: waiting for the workflow on the BE
    // oxlint-disable-next-line no-console
    console.log('[buildViewDataset] match map', pseudoTraversal, traversalMatches);
    return undefined;
};
export const buildViewDatasetCommandDefinition = defineAiControlledCommand<ResolvedPseudoTraversal, BuildViewDatasetContext>({
    name: AiControlledCommandId.BUILD_VIEW_DATASET,
    requiresAppShell: true,
    domain: TRAVERSAL_BUILDING_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                PseudoTraversal: {
                    type: "object",
                    properties: {
                        loadedTraversalId: {
                            type: "string",
                            description: "Set when changing a traversal: its 'loadedTraversalId' from 'loadedTraversals'."
                        },
                        startSet: {
                            $ref: "#/components/schemas/PseudoTraversalStartSet"
                        },
                        paths: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/PseudoTraversalPath"
                            },
                            description: "Example: path [a\u2192b, b\u2192c] exists; d connects to b.\nGood: [a\u2192b, b\u2192c], [a\u2192b, b\u2192d]\nBad: [a\u2192b, b\u2192c], [b\u2192d]\n\nOne path per leaf.\nEach path starts at a name in 'startSetInstanceNames' or at\n'componentType', and lists every step to its leaf, in walk order.\nA path branching off another path repeats that path's steps up to the\nbranch.\nEvery step comes from the user's description or from the changed\ntraversal in 'loadedTraversals'."
                        }
                    },
                    required: [
                        "startSet",
                        "paths"
                    ],
                    description: "The one complete traversal that satisfies the user's request.\nChanging a traversal in 'loadedTraversals': copy from it only the start set\nand the steps that satisfy the request, apply the requested change, and\nsend the whole result: the start set and every path, copied paths included."
                },
                PseudoTraversalStartSet: {
                    type: "object",
                    properties: {
                        componentType: {
                            type: "string",
                            description: "Set when the user names the kind of the start components, or when the\nstart set is copied from a traversal in 'loadedTraversals'.\nValue: the exact entry of 'componentTypeNames' that the user's word\ntranslates to, e.g. \"apps\" \u2192 \"Application\", or the copied 'componentType'."
                        },
                        startSetInstanceNames: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "The start components, named exactly as the user wrote them, or copied\nfrom the 'startSet' of a traversal in 'loadedTraversals'."
                        }
                    },
                    required: [
                        "startSetInstanceNames"
                    ]
                },
                PseudoTraversalPath: {
                    type: "array",
                    items: {
                        $ref: "#/components/schemas/PseudoTraversalTriple"
                    }
                },
                PseudoTraversalTriple: {
                    type: "object",
                    properties: {
                        sourceTypeOrInstanceName: {
                            type: "string",
                            description: "Kind of component: the exact entry of 'componentTypeNames' that the\nuser's word translates to, e.g. \"apps\" \u2192 \"Application\".\nSpecific component: its name exactly as the user wrote it."
                        },
                        referenceType: {
                            type: "string",
                            description: "The relationship as the user names it, e.g. \"owns\"."
                        },
                        targetTypeOrInstanceName: {
                            type: "string",
                            description: "Kind of component: the exact entry of 'componentTypeNames' that the\nuser's word translates to, e.g. \"apps\" \u2192 \"Application\".\nSpecific component: its name exactly as the user wrote it."
                        },
                        direction: {
                            $ref: "#/components/schemas/TripleDirection",
                            description: "'outgoing': the path walks 'sourceTypeOrInstanceName' \u2192 'targetTypeOrInstanceName'.\n'incoming': the path walks 'targetTypeOrInstanceName' \u2192 'sourceTypeOrInstanceName'.\n\"The capabilities Salesforce supports\": Salesforce, supports, Business\nCapability, 'outgoing'.\n\"The people who own Salesforce\": Person, owns, Salesforce, 'incoming'."
                        }
                    },
                    required: [
                        "sourceTypeOrInstanceName",
                        "referenceType",
                        "targetTypeOrInstanceName",
                        "direction"
                    ],
                    description: "One step of a path."
                },
                TripleDirection: {
                    oneOf: [
                        {
                            "const": "outgoing"
                        },
                        {
                            "const": "incoming"
                        }
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/PseudoTraversal"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: BUILD_VIEW_DATASET_CONTEXT_DESCRIPTION,
    isContext: isBuildViewDatasetContext,
    validate: validatePseudoTraversal,
    execute: executeBuildViewDataset,
    isApplied: ({ pseudoTraversal }, { constructedTraversal }) => constructedTraversal === pseudoTraversal,
});
export const buildBuildViewDatasetCommand = (metaModel: MetaModel, loadedStates: LoadedState[], constructedTraversal: PseudoTraversal | null): AiControlledCommand<typeof buildViewDatasetCommandDefinition> => ({
    ...buildViewDatasetCommandDefinition,
    context: {
        componentTypeNames: traversalConstructionOperations.collectComponentTypeNames(metaModel),
        loadedTraversals: traversalConstructionOperations.describeLoadedTraversals(loadedStates),
        constructedTraversal,
    },
});
